import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

const primaryButtonClasses =
  "border-[#464646] bg-neutral-10 text-white shadow-[0px_1px_2px_rgba(0,0,0,0.1)] enabled:hover:bg-neutral-10/80 enabled:hover:text-white aria-expanded:bg-neutral-10/80 disabled:border-transparent disabled:bg-neutral-10/50 disabled:text-white disabled:opacity-100"

const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg border border-transparent font-[450] tracking-[-0.01em] whitespace-nowrap transition-colors outline-none select-none focus-visible:ring-2 focus-visible:ring-accent-9/40 focus-visible:ring-offset-1 focus-visible:ring-offset-neutral-01 disabled:pointer-events-none disabled:opacity-40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: primaryButtonClasses,
        primary: primaryButtonClasses,
        "primary-muted":
          "border-transparent bg-neutral-03 text-neutral-07 shadow-none disabled:border-transparent disabled:bg-neutral-03 disabled:text-neutral-07 disabled:opacity-100",
        outline:
          "border-neutral-04 bg-neutral-01 text-neutral-11 hover:bg-neutral-02 aria-expanded:bg-neutral-02",
        ghost:
          "text-neutral-11 hover:bg-gray-alpha-01 aria-expanded:bg-gray-alpha-01",
        subtle:
          "text-neutral-08 hover:bg-neutral-03 aria-expanded:bg-neutral-03",
        destructive:
          "bg-status-archived/10 text-status-archived hover:bg-status-archived/15",
        surface:
          "border-neutral-04 bg-neutral-01 text-neutral-11 font-body-s-book shadow-1 hover:bg-neutral-02 aria-expanded:bg-neutral-02",
        "marketplace-add":
          "border-0 bg-[#f3f3f3] text-neutral-11 shadow-none hover:bg-neutral-04 aria-expanded:bg-neutral-04",
        link:
          "text-neutral-11 hover:text-neutral-12 aria-expanded:text-neutral-12",
      },
      size: {
        default: "h-8 px-3 text-[14px]",
        sm: "h-7 gap-1 px-2.5 text-[13px] [&_svg:not([class*='size-'])]:size-3.5",
        cta: "h-8 gap-1 px-3 font-caption-1-book",
        pill:
          "h-9 rounded-full px-5 text-[14px] font-medium leading-[1.4] tracking-[-0.28px]",
        form:
          "h-auto min-h-10 w-full rounded-lg py-2 text-sm font-medium leading-[1.4] tracking-[-0.28px]",
        "header-action": "h-6 min-w-[68px] rounded-[8px] px-3 font-caption-1-medium",
        icon: "size-6 px-0",
        "icon-sm": "size-5 px-0 [&_svg:not([class*='size-'])]:size-3.5",
        "marketplace-add":
          "size-6 gap-0 rounded-lg p-0 [&_svg:not([class*='size-'])]:size-[18px]",
        "marketplace-add-text":
          "h-auto gap-1 rounded-lg px-3.5 py-1 font-caption-1-medium",
        "marketplace-connected":
          "size-7 rounded-lg p-0 [&_svg:not([class*='size-'])]:size-5",
      },
    },
    compoundVariants: [
      {
        variant: "surface",
        className: "h-auto px-2 py-1",
      },
      {
        variant: "link",
        className: "h-auto justify-start px-0 border-0 rounded-none",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
